/**
 * AtomLayer - Renders atoms from SVG generation data
 *
 * Renders hitboxes (if provided) and visuals in the same group.
 *
 * Supports opt-in viewport culling (PIPH-72) via `ViewportCullingContext`.
 * When a culling provider is present in the tree, atoms whose positions
 * lie outside the provider's viewport (plus margin) are skipped entirely
 * — no hitbox, no visual, no React reconciliation work. When no provider
 * is present, all atoms render as before.
 */

import React, { memo, useMemo } from 'react';
import { useSVGGenerationData } from '../../contexts/SVGGenerationContext';
import { useViewportCulling } from '../../contexts/ViewportCullingContext';
import { StaticAtom } from '../../motion/components/StaticAtom';
import { isAtomInViewport } from '../../rendering/viewportCulling';
import type { AtomRenderData } from '../../rendering/useSVGGeneration';

export interface AtomLayerProps {
  render?: (atom: AtomRenderData) => React.ReactNode;
  renderHitbox?: (atom: AtomRenderData) => React.ReactNode;
}

// React.memo (PIPH-69): prevents re-renders when `render`/`renderHitbox`
// props are reference-stable. Consumers that want to benefit should wrap
// their callback props in useCallback; consumers using the default
// (no custom render) get the benefit for free.
export const AtomLayer = memo(function AtomLayer({ render, renderHitbox }: AtomLayerProps) {
  const { atoms } = useSVGGenerationData();
  const culling = useViewportCulling();

  // Apply viewport culling when a ViewportCullingProvider is present.
  // Without a provider, `culling` is null and we render everything.
  const visibleAtoms = useMemo(() => {
    if (!culling) return atoms;
    return atoms.filter((atom) =>
      isAtomInViewport(atom, culling.bounds, culling.margin),
    );
  }, [atoms, culling]);

  return (
    <g data-layer="atoms">
      {visibleAtoms.map((atom) => (
        <React.Fragment key={atom.id}>
          {renderHitbox && renderHitbox(atom)}
          {render ? render(atom) : <StaticAtom atom={atom} />}
        </React.Fragment>
      ))}
    </g>
  );
});
